// src/cockpit4/components/KnowledgeRecommendation.tsx
// ============================================
// P0-功能2：知识推荐引擎（知识中心）
//
// 功能：
//   1. 推荐卡片列表（标题 + 摘要 + 推荐理由 + 热度）
//   2. 热门知识排行（基于访问量/最近性计算热度分）
//   3. 新知识推荐（最近新增的笔记，按 mtime 排序）
//   4. 个性化推荐（默认推荐最近 7 天新增 + 热门）
//   5. 点击卡片打开详情（通过 /vault-api/read 读取）
//
// 数据来源：
//   - /vault-api/list?dir=. 递归获取全部笔记列表
//   - /vault-api/read 读取笔记详情
//
// 热度算法：
//   热度分 = 时效性（0-50）+ 内容丰富度（0-50），满分 100
//   时效性：7 天内 50 / 30 天内 30 / 90 天内 15 / 更早 5
//   内容丰富度：按文件大小归一化到 0-50
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Flame,
  Clock,
  Sparkles,
  Loader2,
  RefreshCw,
  FileText,
  ExternalLink,
  X,
  Award,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 笔记元信息（来自 /vault-api/list） */
interface NoteMeta {
  /** 文件名 */
  name: string;
  /** Vault 相对路径 */
  path: string;
  /** 修改时间（ISO 字符串） */
  mtime: string;
  /** 文件大小（字节） */
  size: number;
}

/** 推荐项 */
interface RecommendationItem {
  /** 笔记元信息 */
  note: NoteMeta;
  /** 热度分（0-100） */
  heatScore: number;
  /** 推荐理由 */
  reason: string;
  /** 推荐类型 */
  type: "hot" | "new" | "personalized";
  /** 摘要（懒加载，点击时填充） */
  summary?: string;
}

/** 推荐视图模式 */
type RecommendView = "personalized" | "hot" | "new";

// ============================================
// 主组件
// ============================================

export default function KnowledgeRecommendation() {
  // 全部笔记列表
  const [notes, setNotes] = useState<NoteMeta[]>([]);
  // 加载状态
  const [loading, setLoading] = useState(true);
  // 当前视图
  const [view, setView] = useState<RecommendView>("personalized");
  // 选中的笔记详情（Modal）
  const [selectedNote, setSelectedNote] = useState<{
    title: string;
    path: string;
    content: string;
    loading: boolean;
    heatScore?: number;
    reason?: string;
  } | null>(null);

  // ============================================
  // 递归扫描 Vault 获取全部 .md 文件
  // ============================================
  const scanVault = useCallback(async (): Promise<NoteMeta[]> => {
    const result: NoteMeta[] = [];
    const visited = new Set<string>();
    // BFS 队列，限制最多扫描 5 层深度避免无限递归
    const queue: Array<{ dir: string; depth: number }> = [{ dir: ".", depth: 0 }];
    const MAX_DEPTH = 6;
    const MAX_FILES = 2000;

    while (queue.length > 0 && result.length < MAX_FILES) {
      const { dir, depth } = queue.shift()!;
      if (visited.has(dir)) continue;
      visited.add(dir);

      try {
        const url = `/vault-api/list?dir=${encodeURIComponent(dir)}&ext=`;
        const resp = await fetch(url);
        if (!resp.ok) continue;
        const json = await resp.json();
        if (!json?.success || !Array.isArray(json.files)) continue;

        for (const f of json.files) {
          if (result.length >= MAX_FILES) break;
          if (f.isDirectory) {
            // 限制递归深度，避免路径过深
            if (depth < MAX_DEPTH) {
              queue.push({ dir: f.path, depth: depth + 1 });
            }
          } else if (f.name && f.name.endsWith(".md")) {
            // 仅收录 .md 笔记文件
            result.push({
              name: f.name,
              path: f.path,
              mtime: f.mtime || new Date().toISOString(),
              size: f.size || 0,
            });
          }
        }
      } catch {
        // 单个目录扫描失败，跳过
      }
    }
    return result;
  }, []);

  // ============================================
  // 加载笔记列表
  // ============================================
  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const scanned = await scanVault();
      setNotes(scanned);
    } catch (err) {
      console.warn("[KnowledgeRecommendation] 扫描 Vault 失败:", err);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [scanVault]);

  useEffect(() => {
    void loadNotes();
  }, [loadNotes]);

  // ============================================
  // 计算热度分（时效性 + 内容丰富度）
  // ============================================
  const computeHeatScore = useCallback((note: NoteMeta): number => {
    const now = Date.now();
    const mtime = new Date(note.mtime).getTime();
    const ageDays = Math.max(0, (now - mtime) / (24 * 60 * 60 * 1000));

    // 时效性（0-50）
    let recencyScore = 0;
    if (ageDays <= 7) recencyScore = 50;
    else if (ageDays <= 30) recencyScore = 30;
    else if (ageDays <= 90) recencyScore = 15;
    else recencyScore = 5;

    // 内容丰富度（0-50）：按文件大小归一化
    // 10KB 以上视为内容丰富，按比例给分
    const sizeKB = note.size / 1024;
    const richnessScore = Math.min(Math.round((sizeKB / 10) * 50), 50);

    return Math.min(recencyScore + richnessScore, 100);
  }, []);

  // ============================================
  // 生成推荐理由
  // ============================================
  const computeReason = useCallback((note: NoteMeta, heatScore: number): string => {
    const reasons: string[] = [];
    const now = Date.now();
    const ageDays = Math.max(0, (now - new Date(note.mtime).getTime()) / (24 * 60 * 60 * 1000));
    const sizeKB = note.size / 1024;

    if (ageDays <= 7) {
      reasons.push("最近 7 天新增");
    } else if (ageDays <= 30) {
      reasons.push("本月更新");
    }

    if (heatScore >= 70) {
      reasons.push("热度极高");
    } else if (heatScore >= 50) {
      reasons.push("热度较高");
    }

    if (sizeKB >= 10) {
      reasons.push("内容丰富");
    } else if (sizeKB >= 3) {
      reasons.push("内容适中");
    }

    // 从路径推断分类
    const topDir = note.path.split("/")[0];
    if (topDir) {
      reasons.push(`来自 ${topDir}`);
    }

    return reasons.length > 0 ? reasons.join(" · ") : "知识库收录";
  }, []);

  // ============================================
  // 构建所有推荐项
  // ============================================
  const allRecommendations = useMemo((): RecommendationItem[] => {
    return notes.map((note) => {
      const heatScore = computeHeatScore(note);
      const reason = computeReason(note, heatScore);
      // 默认归类为 hot，后续按视图过滤
      return { note, heatScore, reason, type: "hot" as const };
    });
  }, [notes, computeHeatScore, computeReason]);

  // ============================================
  // 热门排行（按热度分降序）
  // ============================================
  const hotRanking = useMemo(() => {
    return [...allRecommendations]
      .sort((a, b) => b.heatScore - a.heatScore)
      .slice(0, 20)
      .map((r) => ({ ...r, type: "hot" as const }));
  }, [allRecommendations]);

  // ============================================
  // 新知识推荐（按 mtime 降序）
  // ============================================
  const newRecommendations = useMemo(() => {
    const now = Date.now();
    return [...allRecommendations]
      .filter((r) => {
        const ageDays = (now - new Date(r.note.mtime).getTime()) / (24 * 60 * 60 * 1000);
        return ageDays <= 90; // 最近 90 天内新增
      })
      .sort((a, b) => new Date(b.note.mtime).getTime() - new Date(a.note.mtime).getTime())
      .slice(0, 20)
      .map((r) => ({
        ...r,
        type: "new" as const,
        reason: `新增于 ${new Date(r.note.mtime).toLocaleDateString("zh-CN")} · ${r.reason}`,
      }));
  }, [allRecommendations]);

  // ============================================
  // 个性化推荐（最近 7 天新增 + 热门 Top，去重）
  // ============================================
  const personalizedRecommendations = useMemo(() => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;

    // 最近 7 天新增
    const recent = allRecommendations
      .filter((r) => new Date(r.note.mtime).getTime() >= sevenDaysAgo)
      .sort((a, b) => new Date(b.note.mtime).getTime() - new Date(a.note.mtime).getTime())
      .slice(0, 10)
      .map((r) => ({
        ...r,
        type: "personalized" as const,
        reason: `🎯 个性化推荐 · 最近 7 天新增 · ${r.reason}`,
      }));

    // 补充热门 Top（去重）
    const recentPaths = new Set(recent.map((r) => r.note.path));
    const hotFill = hotRanking
      .filter((r) => !recentPaths.has(r.note.path))
      .slice(0, 10 - recent.length)
      .map((r) => ({
        ...r,
        type: "personalized" as const,
        reason: `🎯 个性化推荐 · 热门知识 · ${r.reason}`,
      }));

    return [...recent, ...hotFill];
  }, [allRecommendations, hotRanking]);

  // ============================================
  // 当前视图的推荐列表
  // ============================================
  const currentList = useMemo(() => {
    switch (view) {
      case "hot":
        return hotRanking;
      case "new":
        return newRecommendations;
      case "personalized":
      default:
        return personalizedRecommendations;
    }
  }, [view, hotRanking, newRecommendations, personalizedRecommendations]);

  // ============================================
  // 统计信息
  // ============================================
  const stats = useMemo(() => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const newIn7Days = notes.filter((n) => new Date(n.mtime).getTime() >= sevenDaysAgo).length;
    const avgHeat =
      notes.length > 0
        ? Math.round(allRecommendations.reduce((s, r) => s + r.heatScore, 0) / notes.length)
        : 0;
    const topHeat = hotRanking.length > 0 ? hotRanking[0].heatScore : 0;
    return { total: notes.length, newIn7Days, avgHeat, topHeat };
  }, [notes, allRecommendations, hotRanking]);

  // ============================================
  // 打开笔记详情
  // ============================================
  const openNote = useCallback(
    async (item: RecommendationItem) => {
      setSelectedNote({
        title: item.note.name.replace(/\.md$/i, ""),
        path: item.note.path,
        content: "",
        loading: true,
        heatScore: item.heatScore,
        reason: item.reason,
      });
      try {
        const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(item.note.path)}`);
        if (resp.ok) {
          const json = await resp.json();
          if (json?.success && json.content) {
            setSelectedNote({
              title: item.note.name.replace(/\.md$/i, ""),
              path: item.note.path,
              content: json.content,
              loading: false,
              heatScore: item.heatScore,
              reason: item.reason,
            });
            return;
          }
        }
        setSelectedNote({
          title: item.note.name.replace(/\.md$/i, ""),
          path: item.note.path,
          content: "读取失败：文件不存在或无访问权限",
          loading: false,
          heatScore: item.heatScore,
          reason: item.reason,
        });
      } catch (err) {
        setSelectedNote({
          title: item.note.name.replace(/\.md$/i, ""),
          path: item.note.path,
          content: `读取失败：${(err as Error).message}`,
          loading: false,
          heatScore: item.heatScore,
          reason: item.reason,
        });
      }
    },
    []
  );

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative h-full flex flex-col rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-amber-500/15 border border-amber-500/30">
            <Sparkles size={13} className="text-amber-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">知识推荐引擎</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Recommendation · {stats.total} 篇笔记 · 7 日新增 {stats.newIn7Days} · 平均热度 {stats.avgHeat}
            </p>
          </div>
        </div>

        <button
          onClick={loadNotes}
          disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 size={11} className="text-amber-400 animate-spin" />
          ) : (
            <RefreshCw size={11} className="text-amber-400" />
          )}
          <span className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">
            {loading ? "扫描中..." : "刷新推荐"}
          </span>
        </button>
      </div>

      {/* 视图切换 */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-white/[0.04] bg-white/[0.01]">
        {(
          [
            { id: "personalized", label: "个性化推荐", icon: Sparkles, color: "amber" },
            { id: "hot", label: "热门排行", icon: Flame, color: "red" },
            { id: "new", label: "新知识", icon: Clock, color: "emerald" },
          ] as const
        ).map((tab) => {
          const TIcon = tab.icon;
          const isActive = view === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setView(tab.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[9px] font-mono uppercase tracking-wider transition-all ${
                isActive
                  ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                  : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <TIcon size={10} />
              {tab.label}
            </button>
          );
        })}
        <span className="ml-auto text-[9px] font-mono text-gray-600">
          {currentList.length} 条推荐
        </span>
      </div>

      {/* 推荐列表 */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-500">
            <Loader2 size={20} className="animate-spin text-amber-400 mb-2" />
            <span className="text-[11px] font-mono">正在扫描 Vault 笔记...</span>
          </div>
        ) : currentList.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-12">
            <FileText size={28} className="text-gray-700 mb-2" />
            <p className="text-[11px] font-mono text-gray-500">
              {notes.length === 0 ? "暂无笔记，知识库暂未接入" : "当前视图暂无推荐"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5">
            {currentList.map((item, idx) => (
              <RecommendationCard
                key={`${item.note.path}-${idx}`}
                item={item}
                rank={idx + 1}
                onClick={() => openNote(item)}
              />
            ))}
          </div>
        )}
      </div>

      {/* 底部数据来源 */}
      <div className="px-4 py-1.5 border-t border-white/[0.04]">
        <span className="text-[8px] font-mono text-gray-600">
          数据来源：/vault-api/list 递归扫描 · 热度算法：时效性 50% + 内容丰富度 50%
        </span>
      </div>

      {/* 笔记详情 Modal */}
      <AnimatePresence>
        {selectedNote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
            onClick={() => setSelectedNote(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-3xl max-h-[85vh] flex flex-col rounded-xl border border-amber-500/30 bg-[#0a0e1a] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={14} className="text-amber-400 flex-shrink-0" />
                  <h3 className="text-[14px] font-semibold text-white truncate">
                    {selectedNote.title}
                  </h3>
                  {typeof selectedNote.heatScore === "number" && (
                    <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono bg-amber-500/10 text-amber-400 border border-amber-500/20 flex-shrink-0">
                      <Flame size={8} />
                      {selectedNote.heatScore}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setSelectedNote(null)}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors flex-shrink-0"
                >
                  <X size={16} />
                </button>
              </div>
              {/* 推荐理由 */}
              {selectedNote.reason && (
                <div className="px-5 py-2 border-b border-white/[0.04] bg-amber-500/[0.03]">
                  <div className="flex items-center gap-1.5">
                    <Sparkles size={10} className="text-amber-400/70" />
                    <span className="text-[10px] font-mono text-amber-300/80">
                      {selectedNote.reason}
                    </span>
                  </div>
                </div>
              )}
              <div className="flex-1 min-h-0 overflow-y-auto p-5">
                {selectedNote.loading ? (
                  <div className="flex items-center justify-center py-12 text-gray-500">
                    <Loader2 size={16} className="animate-spin mr-2" />
                    <span className="text-[13px] font-mono">正在从 Vault 加载...</span>
                  </div>
                ) : (
                  <pre className="text-[13px] text-gray-300 whitespace-pre-wrap break-words font-mono leading-relaxed">
                    {selectedNote.content || "暂无内容"}
                  </pre>
                )}
              </div>
              <div className="flex items-center justify-between px-5 py-2 border-t border-white/[0.06] bg-white/[0.01]">
                <span className="text-[10px] font-mono text-gray-600 truncate" title={selectedNote.path}>
                  📁 {selectedNote.path}
                </span>
                <span className="text-[10px] font-mono text-gray-600">数据来源：Obsidian Vault</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：推荐卡片
// ============================================

interface RecommendationCardProps {
  item: RecommendationItem;
  rank: number;
  onClick: () => void;
}

function RecommendationCard({ item, rank, onClick }: RecommendationCardProps) {
  const { note, heatScore, reason, type } = item;
  // 从文件名生成显示标题
  const title = note.name.replace(/\.md$/i, "").replace(/[-_]/g, " ");
  // 从路径提取分类
  const category = note.path.split("/")[0] || "未分类";

  // 热度等级
  const heatLevel =
    heatScore >= 70 ? "high" : heatScore >= 40 ? "medium" : "low";
  const heatColor =
    heatLevel === "high" ? "#EF4444" : heatLevel === "medium" ? "#F59E0B" : "#6B7280";

  // 推荐类型图标
  const TypeIcon = type === "hot" ? Flame : type === "new" ? Clock : Sparkles;
  const typeColor =
    type === "hot" ? "text-red-400" : type === "new" ? "text-emerald-400" : "text-amber-400";

  return (
    <motion.button
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(rank * 0.02, 0.3) }}
      onClick={onClick}
      className="relative text-left rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 hover:bg-amber-500/[0.04] hover:border-amber-500/30 transition-colors group overflow-hidden"
    >
      {/* 排名徽章（仅前 3 名特殊标记） */}
      {rank <= 3 && (
        <div className="absolute top-1 right-1 flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono font-bold"
          style={{
            background: rank === 1 ? "rgba(239,68,68,0.15)" : rank === 2 ? "rgba(245,158,11,0.15)" : "rgba(156,163,175,0.15)",
            color: rank === 1 ? "#EF4444" : rank === 2 ? "#F59E0B" : "#9CA3AF",
          }}
        >
          {rank === 1 && <Award size={8} />}
          TOP {rank}
        </div>
      )}

      <div className="flex items-start gap-2 mb-2">
        {/* 排名序号 */}
        <span className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center text-[9px] font-mono font-bold bg-white/[0.04] text-gray-500">
          {rank}
        </span>
        {/* 标题 */}
        <h4 className="text-[12px] font-medium text-gray-200 line-clamp-2 flex-1 group-hover:text-amber-300 leading-snug">
          {title}
        </h4>
      </div>

      {/* 摘要（路径 + 分类） */}
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-white/[0.04] text-gray-500">
          {category}
        </span>
        <span className="text-[8px] font-mono text-gray-700 truncate" title={note.path}>
          {note.path}
        </span>
      </div>

      {/* 推荐理由 */}
      <div className="flex items-start gap-1 mb-2">
        <TypeIcon size={9} className={`${typeColor} flex-shrink-0 mt-0.5`} />
        <span className="text-[9px] text-gray-500 leading-snug line-clamp-1">
          {reason}
        </span>
      </div>

      {/* 底部：热度 + 时间 */}
      <div className="flex items-center justify-between pt-1.5 border-t border-white/[0.04]">
        <div className="flex items-center gap-1.5">
          {/* 热度进度环（迷你版） */}
          <div className="relative w-5 h-5">
            <svg className="w-5 h-5 -rotate-90" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="8" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1.5" />
              <circle
                cx="10" cy="10" r="8" fill="none"
                stroke={heatColor}
                strokeWidth="1.5"
                strokeDasharray={`${(heatScore / 100) * 50.27} 50.27`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[7px] font-mono font-bold text-white">{heatScore}</span>
            </div>
          </div>
          <span className="text-[8px] font-mono text-gray-600">热度</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock size={8} className="text-gray-600" />
          <span className="text-[8px] font-mono text-gray-600">
            {new Date(note.mtime).toLocaleDateString("zh-CN")}
          </span>
          <ExternalLink size={8} className="text-gray-700 group-hover:text-amber-400 ml-0.5" />
        </div>
      </div>
    </motion.button>
  );
}
